/**
 * O registry PARCIAL do fee-fixo — spec-001 do `project-plataforma-por-chat`,
 * [T016] / US-2.
 *
 * Seis cartões que o módulo já desenhava na tela passam a ser desenháveis
 * DENTRO da conversa, sem que o `chat-message.vue` saiba que o fee-fixo existe:
 * ele resolve a chave no `UI_REGISTRY` (T004) e monta. Cartão novo do módulo é
 * uma entrada aqui — nunca uma linha na bolha.
 *
 * ## Como cada peça se encaixa
 *
 * | peça | onde mora | por quê |
 * |---|---|---|
 * | `component` | `ia/cartoes/*.js` | embrulho que traduz o `value` em props |
 * | `schema` | `ui-registry/schemas/` | CÓPIA byte a byte do `contratos/` do backend |
 * | tool → chave | `LEGACY_TOOL_TO_UI` | as tools do módulo ainda não declaram `ui` |
 *
 * ★ O `component` NÃO aponta para o `.vue` do cartão. Aponta para o embrulho,
 * porque o fio é snake_case e três dos seis cartões esperam o payload embrulhado
 * numa prop só (`filtro`, `pendente`, `preview`) — ver `ia/adaptadores.js`.
 * Apontar direto deixaria o cartão montado e VAZIO, que é a falha calada que
 * esta spec existe para evitar.
 *
 * ★ O `schema` valida o `value` CRU (é ele que o `chat-message` passa a
 * `validarProps`), e por isso é o contrato do backend sem uma vírgula de
 * diferença. A [T020] compara os dois lados byte a byte.
 *
 * ## Os `states` de cada cartão são uma decisão, não uma cópia
 *
 * `input-available` entra em todos: é o estado em que a parte vive enquanto a
 * tool roda, e recusá-lo faria aparecer um cartão de ERRO no meio de todo turno
 * normal, trocado pelo cartão certo um segundo depois. `output-error` e
 * `output-denied` ficam de fora — com UMA exceção, o envio do relatório (ver
 * `ESTADOS_COM_APROVACAO_E_ERRO`): fora dela, nenhum cartão sabe desenhar uma
 * falha, e um cartão que renderiza nada é pior que o `fallback-card` dizendo o
 * que aconteceu. `approval-requested` só nos três que são superfície de
 * autorização (simulação, confirmação e envio) — os outros três nunca o
 * recebem.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { FEATURES } from '@constants/Text'
import { ROUTE_KEYS } from '@constants/navigation-routes'

/**
 * O nome da tool de envio do relatório, no vocabulário do chat.
 *
 * Ele não existia no front: o captador de `apiFeeFixoIa.js` roteia o envio pela
 * MARCA do payload (`value.preview_envio`), não pelo nome — e a marca não serve
 * ao registry, que resolve por tool. Espelho de
 * `aawz_intelligence_v2/tools/tool_fee_fixo.py:147`; o unit desta pasta trava a
 * transcrição contra o arquivo do backend.
 *
 * ⚠️ A tool é de LEITURA por decisão de arquitetura (ADR-001 da spec-005): ela
 * monta o texto, e o CLIQUE do consultor é que gera o PDF e envia. Por isso o
 * nome NÃO está em `WRITE_TOOL_NAMES` lá — de fato não escreve.
 */
export const TOOL_ENVIO_RELATORIO = 'preparar_envio_relatorio_fee_fixo'

/** A chave do módulo no catálogo de navegação (`ROUTE_KEYS.FEE_FIXO`). */
const MODULO = ROUTE_KEYS.FEE_FIXO

/**
 * A flag que gateia os seis cartões — a do MÓDULO, não a da IA dele.
 *
 * O módulo tem duas: `FEE_FIXO` ("Fee Fixo") e `IA_FEE_FIXO` ("IA - Fee Fixo"),
 * esta última aninhada na primeira e usada nas superfícies de IA DENTRO das
 * telas (ensaio, filtro por linguagem natural). Aqui vale a do módulo, que é o
 * que a pergunta do registry faz: *este escritório tem o Fee Fixo?* — sem ele,
 * um cartão do módulo dentro da conversa mostraria dado de coisa que o
 * escritório não contratou, e isso não tem desfazer.
 *
 * Trocar para `IA_FEE_FIXO` seria um gate MAIS restritivo e é uma decisão de
 * produto, não de código: as duas flags são booleanos independentes no `show`
 * (o aninhamento é regra comercial, não invariante), então a troca esconderia
 * o cartão de quem tem o módulo e não tem a flag de IA — com o `fallback-card`
 * dizendo `flag_desligada`, no meio de uma conversa que a pessoa conseguiu ter.
 */
const FLAG_DO_MODULO = FEATURES.FEE_FIXO

// Enquanto a tool roda, e quando o resultado chega. Os dois desfechos de erro
// ficam de fora de propósito (ver o cabeçalho).
const ESTADOS_DE_LEITURA = ['input-available', 'output-available']

// Os três cartões que autorizam alguma coisa também desenham o pedido de
// aprovação — são a própria superfície do two-step.
const ESTADOS_COM_APROVACAO = [
  'input-available',
  'approval-requested',
  'output-available',
]

/**
 * O trio acima MAIS a falha — só do envio do relatório ([G-3], QA rodada-v5
 * it.3, 08/09/2026).
 *
 * O cartão dele é o único dos dez que sabe desenhar o erro: o schema tem a
 * forma ERRO (`anyOf` com `{estado, mensagem_erro}`) e o `.vue` desenha
 * `resolvido === 'erro'` com a mensagem REAL do usecase. O ramo de erro do
 * commit (`tool_fee_fixo_envio_relatorio_confirmar._erro_do_commit`) devolve
 * esse `ui`, e o fio o entrega em `output-error`
 * (`core/sse.py::_mapear_tool_result`, que passou a copiar `ui`/`label` na
 * falha). Sem o estado aqui, `resolverEntrada` recusaria por
 * `estado_nao_suportado` e a tela mostraria o genérico "A ação não pôde ser
 * concluída" — perdendo destinatário e motivo no fim de um envio que falhou.
 *
 * ⚠️ Não copie para os outros cartões: declarar `output-error` sem saber
 * desenhá-lo troca o `fallback-card` (que diz o motivo) por um cartão vazio.
 */
const ESTADOS_COM_APROVACAO_E_ERRO = ESTADOS_COM_APROVACAO.concat([
  'output-error',
])

/**
 * As chaves, no formato `<modulo>/<cartao>` do contrato.
 *
 * Exportadas porque quem preenche o `LEGACY_TOOL_TO_UI` (no `ui-registry`) e
 * quem lê o registry precisam da MESMA string: uma chave redigitada não quebra
 * nada visivelmente — o cartão simplesmente não aparece.
 */
export const CHAVES = {
  SIMULACAO: 'fee-fixo/simulacao-card',
  // ★ SEPARADA da `SIMULACAO` de propósito (G-14, 05/09/2026): aquela é o
  // PREVIEW (exige `requer_confirmacao`/`inputs`/`premissas_declaradas` e
  // existe para o consultor DECIDIR); esta é o DESFECHO, e depois do commit
  // não resta decisão nenhuma. Enquanto ela não existia, o desfecho caía no
  // mapa legado e era recusado pelo schema do preview — "dados fora do
  // contrato" no fim de um processo que gravou.
  SIMULACAO_GRAVADA: 'fee-fixo/simulacao-gravada',
  RECORTE: 'fee-fixo/recorte-card',
  CLIENTE_ATALHO: 'fee-fixo/cliente-atalho-card',
  SUGESTAO_RASCUNHO: 'fee-fixo/sugestao-rascunho',
  CONFIRMACAO: 'fee-fixo/confirmacao',
  ENVIO_RELATORIO: 'fee-fixo/envio-relatorio',
}

/**
 * `fee-fixo/checklist-r2` (spec-010-r2-guiada-pelo-agente-com-transcricao,
 * [T006], US-2/US-4) — a tool `status_da_proposta_r2` emite `ui.component`
 * DIRETO (como as seis chaves acima), sem passar pelo `LEGACY_TOOL_TO_UI`.
 *
 * ★ Fica FORA de `CHAVES` (e portanto fora de `CHAVES_COM_DONO_NO_MODULO`,
 * abaixo) de propósito: as duas superfícies de conversa do módulo (o Guia e
 * o drawer do Especialista) NÃO têm fatia de store própria para este
 * checklist — ele nasce já pelo caminho do registry geral. Incluí-lo em
 * `CHAVES` faria as duas telas descartá-lo do turno (`chavesIgnoradas`) sem
 * nenhum caminho local para desenhar em seu lugar, e o cartão nunca
 * apareceria em lugar nenhum — o mesmo silêncio que `CHAVES_COM_DONO_NO_MODULO`
 * existe para EVITAR, só que na direção contrária.
 */
export const CHAVE_CHECKLIST_R2 = 'fee-fixo/checklist-r2'

/**
 * `fee-fixo/deck-r2-protocolo` (spec-010-r2-guiada-pelo-agente-com-transcricao,
 * [T010]/[T033], complemento da [T006]) — a tool two-step `gerar_proposta_r2`
 * emite este `ui.component` DIRETO, nas duas etapas do protocolo (`preview`
 * sem side-effect, `protocolo` depois do `confirmar=true`).
 *
 * ★ Fica FORA de `CHAVES` (e portanto fora de `CHAVES_COM_DONO_NO_MODULO`),
 * pela MESMA razão de `CHAVE_CHECKLIST_R2`: nenhuma das duas superfícies de
 * conversa tem fatia de store própria para o deck da R2 — incluí-la faria as
 * duas telas descartarem a parte sem ter onde desenhar em seu lugar.
 */
export const CHAVE_DECK_R2_PROTOCOLO = 'fee-fixo/deck-r2-protocolo'

/**
 * `fee-fixo/campos-reuniao` (spec-010-r2-guiada-pelo-agente-com-transcricao,
 * G-16 do QA it.5, complemento do [T006]) — a tool two-step
 * `popular_campos_da_reuniao` emite este `ui.component` DIRETO, nos dois
 * estados do protocolo: `approval-requested` para o preview (`value` traz
 * `requer_confirmacao` via `envelope_de_preview`) e `output-available` para
 * o desfecho, depois do `confirmar=true` já interceptado no servidor (G-13
 * — o mesmo commit que grava lê o `payload` capturado no preview, nunca uma
 * re-extração).
 *
 * ★ Fica FORA de `CHAVES` (e portanto fora de `CHAVES_COM_DONO_NO_MODULO`),
 * pela MESMA razão de `CHAVE_CHECKLIST_R2`/`CHAVE_DECK_R2_PROTOCOLO`: nenhuma
 * das duas superfícies de conversa do módulo tem fatia de store própria para
 * os campos da reunião — incluí-la faria as duas telas descartarem a parte
 * do turno sem ter onde desenhar em seu lugar.
 */
export const CHAVE_CAMPOS_REUNIAO = 'fee-fixo/campos-reuniao'

/**
 * `fee-fixo/exportar-planilha` (spec-018-roteiro-do-video-responde-de-verdade,
 * [T008] no front / [T008b] no backend, Q5 do roteiro) — a tool de LEITURA
 * `exportar_planilha` gera o `.xlsx` e devolve o base64; o cartao e o unico
 * lugar da conversa em que o consultor consegue BAIXAR o arquivo.
 *
 * ★ Medido antes de existir: a tool devolvia o base64 so' no `value`, sem
 * `ui` nenhum. O modelo escrevia "planilha gerada" e nao havia botao em lugar
 * nenhum — o `value` de uma tool nunca vira tela sozinho.
 *
 * ★ `ESTADOS_DE_LEITURA`, nao `ESTADOS_COM_APROVACAO`: nao ha nada a
 * confirmar. O backend so' emite `ui` quando HOUVE arquivo (`gerado=true`);
 * na carteira vazia e no ramo de erro o cartao nao nasce, para nao existir
 * botao morto.
 *
 * ★ Fica FORA de `CHAVES` (e de `CHAVES_COM_DONO_NO_MODULO`), pela mesma
 * razao das tres irmas acima: nenhuma das duas superficies de conversa do
 * modulo tem fatia de store para a planilha — inclui-la faria as duas telas
 * descartarem a parte sem ter onde desenhar em seu lugar.
 */
export const CHAVE_EXPORTAR_PLANILHA = 'fee-fixo/exportar-planilha'
export const CHAVE_APRESENTACAO = 'fee-fixo/apresentacao'

/**
 * `fee-fixo/ranking-profissionais` (spec-009-quem-migrar-profissionais,
 * [T033] no backend / [T040] aqui) — a tool `priorizar_profissionais_fee_fixo`
 * emite este `ui.component` DIRETO, gestor-only (o mesmo gate de
 * `montar_ranking_de_profissionais`: 403 antes de qualquer leitura).
 *
 * ★ Fica FORA de `CHAVES` (e de `CHAVES_COM_DONO_NO_MODULO`), pela mesma
 * razao das irmas acima: nenhuma das duas superficies de conversa do modulo
 * tem fatia de store para "o ranking de TODOS os profissionais" — inclui-la
 * faria as duas telas descartarem a parte sem ter onde desenhar em seu lugar.
 */
export const CHAVE_RANKING_PROFISSIONAIS = 'fee-fixo/ranking-profissionais'

/**
 * As seis chaves cujo dono, DENTRO do módulo, é a própria tela — [I-11] do
 * review de front it.2 do PR #2086.
 *
 * As duas superfícies de conversa do Fee Fixo (o Guia e o drawer do
 * Especialista) já desenham estes cartões pelo canal LEGADO: o `tool_result`
 * cai numa fatia da store (`simulacaoPendente`, `filtroSugerido`,
 * `clientesCitados`, `pendingConfirmation`) e a tela monta o componente. Quando
 * o [I-1] as fez montar TODAS as partes do turno, a MESMA tool passou a
 * desenhar duas vezes — o cartão da tela e o do registry, um debaixo do outro.
 *
 * É a mesma regra do `CHAVES_DE_GATE_LEGADO` do `chat-message.vue`, com o mesmo
 * motivo: onde a chave já tem dono fora do renderizador, a parte é descartada —
 * senão a mesma mensagem teria duas origens para o mesmo cartão.
 *
 * ★ **DERIVADA de `CHAVES`, nunca redigitada.** Uma lista literal com uma chave
 * a mais silenciaria um cartão que não duplica; com uma a menos, a duplicação
 * volta — e as duas falhas são caladas, porque nada estoura quando a chave não
 * casa.
 *
 * ⚠️ O que isto NÃO faz: dar casa a estes cartões numa tela que não os desenha.
 * O drawer do Especialista mora sobre todas as telas do módulo e só desenha o
 * `envio-relatorio` dele mesmo; nas outras cinco chaves, quem desenha continua
 * sendo a tela dona (o Guia, a Simulação, a aba Clientes) — que é como era
 * antes do [I-1]. Levar o cartão para dentro do drawer é decisão de produto,
 * não de review: exige dizer o que ele faz ali (o "Salvar" da simulação, o
 * "Aplicar" do recorte) fora da tela que executa a ação.
 */
export const CHAVES_COM_DONO_NO_MODULO = Object.values(CHAVES)

/**
 * ⚠️ O mapa `tool → chave` NÃO mora aqui: ele é o `LEGACY_TOOL_TO_UI` do
 * `ui-registry/index.js`, e é lá que as oito tools do módulo são ligadas às
 * seis chaves acima (a priorização e a leitura de estado abrem o MESMO atalho
 * de cliente — duas formas do mesmo payload; as duas escritas abrem a MESMA
 * confirmação — mesmo shape, copy diferente).
 *
 * Uma cópia do mapa aqui seria a segunda fonte do mesmo fato, e a divergência
 * entre elas é calada: o `tool_result` chega sem dono e nenhum cartão aparece.
 * Quem trava a transcrição é `registry.unit.js`, contra as constantes de tool
 * do módulo E contra o arquivo de tools do backend.
 */

export default {
  [CHAVES.SIMULACAO]: {
    component: () => import('@modules/fee-fixo/ia/cartoes/cartao-simulacao'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-fee-fixo-simulacao-card.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_COM_APROVACAO,
    // A cópia PERSISTIDA (F5, conversa reaberta) chega como `somente-leitura`:
    // é o sinal que impede o embrulho de depositar os números de um preview
    // histórico — só o preview VIVO deposita (`cartoes/cartao-simulacao.js`).
    historicoSomenteLeitura: true,
    label: 'Simulação de fee fixo',
  },
  [CHAVES.SIMULACAO_GRAVADA]: {
    component: () =>
      import('@modules/fee-fixo/ia/cartoes/cartao-simulacao-gravada'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-fee-fixo-simulacao-gravada.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    // ★ Sem `approval-requested`: o desfecho nunca o recebe — quando há
    // aprovação pendente quem desenha é o cartão do PREVIEW. Incluí-lo aqui
    // faria os dois cartões coexistirem no mesmo estado.
    states: ESTADOS_DE_LEITURA,
    label: 'Cenário salvo no Fee Lab',
  },
  [CHAVES.RECORTE]: {
    component: () => import('@modules/fee-fixo/ia/cartoes/cartao-recorte'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-fee-fixo-recorte-card.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Recorte de clientes sugerido',
  },
  [CHAVES.CLIENTE_ATALHO]: {
    component: () =>
      import('@modules/fee-fixo/ia/cartoes/cartao-cliente-atalho'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-fee-fixo-cliente-atalho-card.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Clientes citados na resposta',
  },
  [CHAVES.SUGESTAO_RASCUNHO]: {
    component: () =>
      import('@modules/fee-fixo/ia/cartoes/cartao-sugestao-rascunho'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-fee-fixo-sugestao-rascunho.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_DE_LEITURA,
    label: 'Ajustes sugeridos para o cenário',
  },
  [CHAVES.CONFIRMACAO]: {
    component: () => import('@modules/fee-fixo/ia/cartoes/cartao-confirmacao'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-fee-fixo-confirmacao.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    states: ESTADOS_COM_APROVACAO,
    label: 'Confirmação da escrita no funil',
  },
  [CHAVES.ENVIO_RELATORIO]: {
    component: () =>
      import('@modules/fee-fixo/ia/cartoes/cartao-envio-relatorio'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-fee-fixo-envio-relatorio.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    // ★ O ÚNICO com `output-error` — ver `ESTADOS_COM_APROVACAO_E_ERRO`.
    states: ESTADOS_COM_APROVACAO_E_ERRO,
    label: 'Envio do relatório ao cliente',
  },
  [CHAVE_CHECKLIST_R2]: {
    component: () =>
      import('@modules/fee-fixo/ia/cartoes/cartao-checklist-r2'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-fee-fixo-checklist-r2.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    // Leitura pura — `output-error` fica de fora (o cartão não sabe desenhar
    // falha; vira `fallback-card`, que sabe dizer o motivo).
    states: ESTADOS_DE_LEITURA,
    label: 'Checklist da proposta R2',
  },
  [CHAVE_DECK_R2_PROTOCOLO]: {
    component: () =>
      import('@modules/fee-fixo/ia/cartoes/cartao-deck-r2-protocolo'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-fee-fixo-deck-r2-protocolo.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    // Escrita two-step: `preview` chega em `approval-requested` (o `value`
    // tem `requer_confirmacao`, e `core/sse.py::estado_do_tool_result` marca
    // o estado por isso); `protocolo` chega em `output-available`, depois do
    // `confirmar=true`. Mesmo trio de `CHAVES.SIMULACAO`/`CHAVES.CONFIRMACAO`.
    states: ESTADOS_COM_APROVACAO,
    // A cópia PERSISTIDA (conversa reaberta) chega como `somente-leitura`:
    // vira o selo "proposta enviada" em vez de reabrir Confirmar/Cancelar
    // sobre um preview já consumido (`renderiza-partes.js::montarCartao`).
    historicoSomenteLeitura: true,
    label: 'Protocolo do deck da proposta R2',
  },
  [CHAVE_CAMPOS_REUNIAO]: {
    component: () =>
      import('@modules/fee-fixo/ia/cartoes/cartao-campos-reuniao'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-fee-fixo-campos-reuniao.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    // Escrita two-step: `preview` chega em `approval-requested`; o desfecho
    // chega em `output-available`, depois do `confirmar=true` interceptado
    // no servidor (G-13). Mesmo trio de `CHAVES.CONFIRMACAO`/
    // `CHAVE_DECK_R2_PROTOCOLO`.
    states: ESTADOS_COM_APROVACAO,
    // ★ Byte a byte o `LABEL` de `ToolPopularCamposDaReuniao`
    // (`tool_popular_campos_reuniao.py`) — `Text.tool-labels.unit.js` deriva
    // essa junção do fonte do backend (schema → tool emissora única) e
    // reprova se os dois textos divergirem sobre a MESMA tool.
    label: 'Lendo os campos que a reunião revelou...',
  },
  [CHAVE_APRESENTACAO]: {
    component: () => import('@modules/fee-fixo/ia/cartoes/cartao-apresentacao'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-fee-fixo-apresentacao.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    // Leitura pura: o `ui` chega no DISPARO (a geração é assíncrona) e o
    // cartão acompanha sozinho até um estado terminal. Nada a aprovar — o
    // clique em baixar é 100% do browser.
    states: ESTADOS_DE_LEITURA,
    // A cópia PERSISTIDA (F5, conversa reaberta) não re-monta o poller nem
    // baixa sozinha: chega ao cartão como `somente-leitura` e vira o botão
    // "Baixar" (`renderiza-partes.js::montarCartao`).
    historicoSomenteLeitura: true,
    // ★ Byte a byte o `LABEL` de `ToolGerarApresentacao`
    // (`aawz_fee_fixo/tools/tool_gerar_apresentacao.py`).
    label: 'Gerando a apresentação...',
  },
  [CHAVE_EXPORTAR_PLANILHA]: {
    component: () =>
      import('@modules/fee-fixo/ia/cartoes/cartao-exportar-planilha'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-fee-fixo-exportar-planilha.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    // Leitura pura: o `ui` so' chega em `output-available`, com o arquivo
    // dentro. Nada a aprovar, nada a responder ao modelo — o clique em
    // "Baixar planilha" e' 100% do browser (`downloadBase64`).
    states: ESTADOS_DE_LEITURA,
    // ★ Byte a byte o `LABEL` de `ToolExportarPlanilha`
    // (`tool_exportar_planilha.py`) — `Text.tool-labels.unit.js` deriva essa
    // juncao do fonte do backend e reprova se os dois divergirem.
    label: 'Montando a planilha...',
  },
  [CHAVE_RANKING_PROFISSIONAIS]: {
    component: () =>
      import('@modules/fee-fixo/ia/cartoes/cartao-ranking-profissionais'),
    schema: () =>
      import(
        '@modules/aawz-intelligence-v2/ui-registry/schemas/ui-fee-fixo-ranking-profissionais.schema.json'
      ),
    module: MODULO,
    feature: FLAG_DO_MODULO,
    // Leitura pura: o gate de gestor ja' rodou no nucleo
    // (montar_ranking_de_profissionais, 403 antes de qualquer leitura), e a
    // tool nao tem nada a confirmar/desfazer — so' o ranking.
    states: ESTADOS_DE_LEITURA,
    // ★ Byte a byte o `LABEL` de `ToolPriorizarProfissionaisFeeFixo`
    // (`tool_fee_fixo_profissionais.py`) — `Text.tool-labels.unit.js` deriva
    // essa juncao do fonte do backend e reprova se os dois divergirem.
    label: 'Lendo as carteiras do escritório...',
  },
}
